import React, { useState, useEffect } from "react";
import { User } from "../../types";
import '../../styles/main.css';
import { configureEspWebInstallButton } from './configureEspWebInstallButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { API_BASE_URL } from '../../config/api';
import ModelSelect from './ModelSelect';
import { getHardwareTableDefaults } from './hardwarePartitionOptions';

interface BinFile {
  path: string;
  offset: number;
}

interface Release {
  _id: string;
  model: string;
  version: string;
  type: 'dev' | 'beta' | 'prod' | 'depr' | 'factory';
  hardware?: string;
  forceUpdate: boolean;
  clearState: boolean;
  enabled: boolean;
  bins: BinFile[];
  createdBy: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface Model {
  _id: string;
  name: string;
  factory_name: string;
  code: string;
  description: string;
}

interface FactoryIdentifyPayload {
  serial: string;
  model: string;
  mac_address: string;
  versions?: {
    firmware?: string;
    bootloader?: string;
    partition_table?: string;
    partitions?: string;
    pcb?: string;
    silicon?: string;
  };
}

interface FactoryFlasherProps {
  user: User;
}

const FactoryFlasher: React.FC<FactoryFlasherProps> = ({ user }) => {
    const MANIFEST_BASE_URL = API_BASE_URL || 'https://firmware.fluoraplant.com';

  const [models, setModels] = useState<Model[]>([]);
  const [releases, setReleases] = useState<Release[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<string>("null");
  const [selectedHardware, setSelectedHardware] = useState<string>("null");
  const [selectedFirmware, setSelectedFirmware] = useState<string>("null");
  const [selectedRelease, setSelectedRelease] = useState<Release | null>(null);
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  const [releasesLoading, setReleasesLoading] = useState<boolean>(false);
  const [showHardwareInfoModal, setShowHardwareInfoModal] = useState<boolean>(false);
  const [selectedBaudRate, setSelectedBaudRate] = useState<number>(2000000);
  const [factoryRegistrationMessage, setFactoryRegistrationMessage] = useState<string>('');
  const [factoryRegistrationError, setFactoryRegistrationError] = useState<string>('');
  const [factoryRegistrationInProgress, setFactoryRegistrationInProgress] = useState<boolean>(false);

  // Fetch all models (initial load)
  useEffect(() => {
    const fetchModels = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/models`, { headers: { 'Accept': 'application/json' } });
        if (!response.ok) throw new Error("Failed to fetch models");
        const data = await response.json();
        setModels(data.models || []);
      } catch (error: any) {
        // handle error
      } finally {
        setInitialLoading(false);
      }
    };
    fetchModels();
  }, [API_BASE_URL]);

  // Fetch releases when a model is selected
  useEffect(() => {
    if (selectedProduct && selectedProduct !== "null") {
      const fetchReleases = async () => {
        setReleasesLoading(true);
        try {
          const response = await fetch(`${API_BASE_URL}/releases/${encodeURIComponent(selectedProduct)}`, { headers: { 'Accept': 'application/json' } });
          if (!response.ok) throw new Error("Failed to fetch releases");
          const data = await response.json();
          setReleases(data.releases);
        } catch (error: any) {
          // handle error
        } finally {
          setReleasesLoading(false);
        }
      };
      fetchReleases();
    } else {
      setReleases([]);
    }
  }, [selectedProduct, API_BASE_URL]);

  // Baud rate options
  const baudRateOptions = [
    { value: 38400, label: "38400" },
    { value: 57600, label: "57600" },
    { value: 115200, label: "115200" },
    { value: 230400, label: "230400" },
    { value: 460800, label: "460800" },
    { value: 921600, label: "921600" },
    { value: 2000000, label: "2000000 (2 Mbps) (Default)" }
  ];

  // Get available hardware revisions for the selected product
  const getAvailableHardwareRevisions = () => {
    if (selectedProduct === "null") return [];
    const factoryReleases = releases.filter(
      r => r.model === selectedProduct && r.type === "factory" && r.enabled
    );
    // Unique hardware revisions
    return [...new Set(factoryReleases.map(r => r.hardware).filter(Boolean))];
  };

  // Get available firmware versions for selected product and hardware
  const getAvailableFirmwareVersions = () => {
    if (selectedProduct === "null" || selectedHardware === "null") return [];
    return releases
      .filter(
        r =>
          r.model === selectedProduct &&
          r.type === "factory" &&
          r.enabled &&
          r.hardware === selectedHardware
      )
      .sort((a, b) =>
        b.version.localeCompare(a.version, undefined, { numeric: true, sensitivity: "base" })
      );
  };

  // Handle product selection change
  const handleProductChange = (productCode: string) => {
    const selectedModel = models.find(m => m.code === productCode);
    if (selectedModel) {
      setSelectedProduct(selectedModel.name);
      setSelectedHardware("null");
      setSelectedFirmware("null");
      setSelectedRelease(null);
    }
  };

  // Handle hardware revision selection change
  const handleHardwareChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedHardware(e.target.value);
    setSelectedFirmware("null");
    setSelectedRelease(null);
  };

  // Handle baud rate selection change
  const handleBaudRateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedBaudRate(parseInt(e.target.value, 10));
  };

  // Handle firmware version selection change
  const handleFirmwareChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const firmwareVersion = e.target.value;
    setSelectedFirmware(firmwareVersion);

    if (
      selectedProduct !== "null" &&
      selectedHardware !== "null" &&
      firmwareVersion !== "null"
    ) {
      const firmwareInfo = releases.find(
        r =>
          r.model === selectedProduct &&
          r.type === "factory" &&
          r.enabled &&
          r.hardware === selectedHardware &&
          r.version === firmwareVersion
      );
      setSelectedRelease(firmwareInfo || null);
    } else {
      setSelectedRelease(null);
    }
  };

  // Ref for the install button container
  const installButtonRef = React.useRef<HTMLDivElement>(null);

  const saveFactoryDeviceRecord = React.useCallback(async (identify: FactoryIdentifyPayload) => {
    setFactoryRegistrationMessage('Registering device...');
    setFactoryRegistrationError('');
    setFactoryRegistrationInProgress(true);

    try {
      const response = await fetch(`${API_BASE_URL}/devices/factory-registration`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          serial: identify.serial,
          model: identify.model || selectedProduct,
          version: identify.versions?.firmware || selectedRelease?.version,
          type: 'prod',
          hardware_revision: selectedHardware !== 'null' ? selectedHardware : identify.versions?.pcb,
          mac_address: identify.mac_address,
          factory: {
            firmware: {
              version: identify.versions?.firmware || selectedRelease?.version,
              releaseId: selectedRelease?._id,
            },
            bootloader: {
              version: identify.versions?.bootloader,
            },
            partition_table: {
              version: identify.versions?.partition_table || identify.versions?.partitions,
            },
            esp32_chip_revision: {
              version: identify.versions?.silicon,
            },
          },
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          throw new Error('Access denied. Refresh the page and log in as an admin or factory user, then try registering again.');
        }
        throw new Error(data.message || 'Failed to save factory device record');
      }

      setFactoryRegistrationMessage(
        data.created === false
          ? `Factory registration updated for existing device ${identify.serial}.`
          : `Factory device record created for ${identify.serial}.`
      );
    } catch (error: any) {
      setFactoryRegistrationError(error.message || 'Failed to save factory device record');
      setFactoryRegistrationMessage('');
    } finally {
      setFactoryRegistrationInProgress(false);
    }
  }, [selectedHardware, selectedProduct, selectedRelease]);

  // Create/update the install button element
  React.useEffect(() => {
    if (!installButtonRef.current) return;
    
    // Clear existing content
    installButtonRef.current.innerHTML = '';
    
    if (!selectedRelease) {
      return;
    }

    // Build the manifest dynamically so the factory image includes every
    // partition (bootloader, partition table, boot_app0, app) plus a freshly
    // generated hardware table. The factory flasher marks factoryTest=0 because
    // the factory test is performed AFTER flashing the device.
    const hwDefaults = getHardwareTableDefaults(selectedProduct);
    const manifestParams = new URLSearchParams({
      model: selectedProduct,
      version: selectedRelease.version,
      type: 'factory',
      hardware: selectedHardware,
      bootloader: '1',
      partitions: '1',
      bootApp0: '1',
      hardwareTable: '1',
      pcbRevision: selectedHardware,
      pcbType: hwDefaults.pcbType,
      modelType: hwDefaults.modelType,
      factoryTest: '0',
    });

    // Create the custom element
    const installButton = document.createElement('esp-web-install-button');
    configureEspWebInstallButton(installButton, {
      manifest: `${MANIFEST_BASE_URL}/manifest/build?${manifestParams.toString()}`,
      baudRate: selectedBaudRate,
      installHeadlineFast: 'Install (烧录)',
    });
    installButton.addEventListener('device-identify', ((event: Event) => {
      const detail = (event as CustomEvent<FactoryIdentifyPayload>).detail;
      if (detail?.serial) {
        saveFactoryDeviceRecord(detail);
      }
    }) as EventListener);
    
    // Create the button element
    const button = document.createElement('button');
    button.setAttribute('slot', 'activate');
    button.className = 'btn btn-primary btn-lg';
    button.textContent = 'Connect To Device';
    
    installButton.appendChild(button);
    installButtonRef.current.appendChild(installButton);
  }, [selectedRelease, selectedProduct, selectedHardware, selectedBaudRate, saveFactoryDeviceRecord, MANIFEST_BASE_URL]);

  // Install button container
  const InstallButton = <div ref={installButtonRef} />;

  if (initialLoading) {
    return (
      <div className="container-fluid mt-3">
        <div className="row mb-3">
          <div className="col">
            <h3>Factory Firmware Flasher</h3>
          </div>
        </div>
        <div className="row">
          <div className="col-md-6 mx-auto text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading firmware data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-3">
      <div className="row mb-3">
        <div className="col">
          <h3>Firmware Flasher</h3>
        </div>
      </div>
      <div className="row g-4 align-items-start">
        <div className="col-12 col-lg-4">
          <div>
            {/* Step 1: Product Selection */}
            <div className="mb-4">
              <label htmlFor="product_model" className="form-label fw-bold">Model</label>
              <ModelSelect
                id="product_model"
                className="mb-3"
                models={models}
                value={selectedProduct === "null" ? "" : models.find(m => m.name === selectedProduct)?.code || ""}
                onChange={handleProductChange}
                useFactoryName
              />
            </div>
            {/* Step 2: Hardware Version Selection */}
            <div className="mb-4">
              <label htmlFor="hardware_version" className="form-label fw-bold me-0">
                Hardware Version
                <button
                  type="button"
                  className="btn btn-link p-0 ms-1 align-middle"
                  onClick={() => setShowHardwareInfoModal(true)}
                  style={{ color: "inherit", textDecoration: "none", position: "relative", top: "-1px" }}
                >
                  <FontAwesomeIcon
                    icon={faInfoCircle}
                    className="text-secondary"
                    size="lg"
                    aria-label="More information about hardware versions"
                  />
                </button>
              </label>
              <select
                id="hardware_version"
                className="form-select mb-3"
                onChange={handleHardwareChange}
                value={selectedHardware === "null" ? "" : selectedHardware}
                disabled={selectedProduct === "null"}
              >
                <option value="null">Choose A Hardware Version...</option>
                {getAvailableHardwareRevisions().map((version, index) => (
                  <option key={index} value={version as string}>
                    {version}
                  </option>
                ))}
              </select>
              {/* Hardware Info Modal */}
              {showHardwareInfoModal && (
                <>
                  <div
                    className="modal-backdrop show"
                    onClick={() => setShowHardwareInfoModal(false)}
                  ></div>
                  <div
                    className="modal d-block"
                    tabIndex={-1}
                    role="dialog"
                    aria-labelledby="hardwareInfoModalLabel"
                    aria-hidden="true"
                  >
                    <div className="modal-dialog" role="document">
                      <div className="modal-content">
                        <div className="modal-header">
                          <h5 className="modal-title" id="hardwareInfoModalLabel">Hardware Version</h5>
                          <button
                            type="button"
                            className="btn-close"
                            aria-label="Close"
                            onClick={() => setShowHardwareInfoModal(false)}
                          ></button>
                        </div>
                        <div className="modal-body">
                        <h5>How to Determine Your Hardware Revision</h5>
                          <p>The hardware revision can be found on the PCB located inside of the product, printed under the logo.</p>
                          <img
                            src="/hardware-revision-location.png"
                            alt="Location of the hardware revision on the Fluora and Monos PCBs"
                            className="img-fluid rounded border mb-3"
                          />
                          <p className="alert alert-warning">
                            <strong>Important:</strong> Flashing firmware for an incorrect hardware revision may cause your device to malfunction.
                            If you're unsure, please contact customer support.
                          </p>
                        </div>
                        <div className="modal-footer">
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => setShowHardwareInfoModal(false)}
                          >
                            Got it
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
            {/* Step 3: Firmware Version Selection */}
            <div className="mb-4">
              <label htmlFor="firmware_version" className="form-label fw-bold">Firmware Version</label>
              <select
                id="firmware_version"
                className="form-select mb-3"
                onChange={handleFirmwareChange}
                value={selectedFirmware === "null" ? "" : selectedFirmware}
                disabled={selectedProduct === "null" || selectedHardware === "null" || releasesLoading}
              >
                <option value="null">Choose A Version...</option>
                {releasesLoading ? (
                  <option>Loading...</option>
                ) : (
                  getAvailableFirmwareVersions().map((release, index) => (
                    <option key={index} value={release.version}>
                      {release.version}
                    </option>
                  ))
                )}
              </select>
            </div>
            {/* Step 4: Baud Rate Selection */}
            <div className="mb-4">
              <label htmlFor="baud_rate" className="form-label fw-bold">Baud Rate</label>
              <select
                id="baud_rate"
                className="form-select mb-3"
                onChange={handleBaudRateChange}
                value={selectedBaudRate}
              >
                {baudRateOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            {/* Step 5: Connect to Device Button */}
            <div className="mb-4">
              {InstallButton}
              {factoryRegistrationMessage && (
                <div className={`alert ${factoryRegistrationInProgress ? 'alert-info' : 'alert-success'} mt-3 mb-0 py-2`}>
                  {factoryRegistrationMessage}
                </div>
              )}
              {factoryRegistrationError && (
                <div className="alert alert-danger mt-3 mb-0 py-2">
                  {factoryRegistrationError}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="col-12 col-lg-4">
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">Quick Instructions</h5>
            </div>
            <div className="card-body">
              <ol className="mb-0" style={{ fontSize: "0.9em" }}>
                <li>Access USB connector</li>
                <li>Plug USB in to computer and device</li>
                <li>Confirm device is powered on and red indicator LED light is illuminated</li>
                <li>Select Model, Hardware, and Firmware</li>
                <li>Click "Connect To Device"</li>
                <li>Select "USB"</li>
                <li>Follow instructions on screen</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FactoryFlasher;